/**
 * Orders view component.
 * @module components/View/OrdersView
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Input,
  Label,
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { Link, useHistory } from 'react-router-dom';

import { Icon } from '@plone/volto/components';
import { getVocabulary } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';

import downSVG from '@plone/volto/icons/down-key.svg';
import upSVG from '@plone/volto/icons/up-key.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import addSVG from '@plone/volto/icons/add.svg';

const formatDate = (date) => {
  if (!date) return '';
  const parsed = new Date(date);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  let hours = parsed.getHours();
  const minutes = parsed.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const minutesStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
  return `${year}-${month}-${day} ${hours}:${minutesStr} ${ampm}`;
};

/**
 * Orders view component class.
 * @function OrdersView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const OrdersView = ({ content }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: 'contacts' }));
    dispatch(getVocabulary({ vocabNameOrURL: 'payment' }));
  }, [dispatch]);

  const contactsVocabulary = useSelector(
    (state) => state.vocabularies?.contacts?.items || [],
  );
  const paymentVocabulary = useSelector(
    (state) => state.vocabularies?.payment?.items || [],
  );

  const contactTitle = (value) =>
    contactsVocabulary.find((item) => item.value === value)?.label || value;
  const paymentTitle = (value) =>
    paymentVocabulary.find((item) => item.value === value)?.label || value;
  const statusClass = (payment) => {
    if (payment === 'cancelled') return 'status-cancelled';
    if (payment === 'open') return 'status-open';
    return 'status-paid';
  };

  let items = content.items.map((item) => ({
    ...item,
    contact: contactTitle(item.contact),
    status: paymentTitle(item.payment),
  }));

  // Filtering
  const [id, setId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTill, setDateTill] = useState('');
  const [contact, setContact] = useState('');
  const [channel, setChannel] = useState('');

  const idRef = useRef(null);
  const contactRef = useRef(null);
  const channelRef = useRef(null);

  const filterField = (items, field, value) =>
    value === ''
      ? items
      : (items = items.filter(
          (item) =>
            item[field] &&
            item[field].toLowerCase().indexOf(value.toLowerCase()) !== -1,
        ));

  const filterDateRange = (items, from, till) => {
    let filtered = items;
    if (from) {
      const fromTime = new Date(from).getTime();
      filtered = filtered.filter(
        (item) =>
          item.order_date && new Date(item.order_date).getTime() >= fromTime,
      );
    }
    if (till) {
      // "Till" is a date with no time, so make it inclusive of the whole
      // day rather than cutting off at its midnight.
      const tillTime = new Date(till).getTime() + 24 * 60 * 60 * 1000 - 1;
      filtered = filtered.filter(
        (item) =>
          item.order_date && new Date(item.order_date).getTime() <= tillTime,
      );
    }
    return filtered;
  };

  items = filterField(items, 'id', id);
  items = filterDateRange(items, dateFrom, dateTill);
  items = filterField(items, 'contact', contact);
  items = filterField(items, 'channel', channel);

  // Sorting
  const [sort, setSort] = useState('id');
  const [dir, setDir] = useState('desc');

  items = items.sort((a, b) =>
    (dir === 'asc' ? a[sort] > b[sort] : a[sort] < b[sort]) ? 1 : -1,
  );

  const onSort = (value) => {
    if (sort === value) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(value);
      setDir('asc');
    }
  };

  const SortIcon = () => (
    <Icon
      name={dir === 'desc' ? upSVG : downSVG}
      className="icon"
      size="20px"
    />
  );

  return (
    <article id="orders">
      <div className="button-toolbar">
        <Link
          className="circular-button primary float-left"
          to={`${flattenToAppURL(content['@id'])}/add?type=Order`}
        >
          <Icon name={addSVG} size="24px" />
        </Link>
        <div className="button-toolbar-title">
          Orders <Label circular>{items.length}</Label>
        </div>
      </div>
      <div className="view-padding">
        <div className="scrollable-table">
          <Table striped celled selectable>
            <TableHeader>
              <TableRow className="tools-header">
                <TableHeaderCell onClick={() => onSort('id')} width="2">
                  Order # {sort === 'id' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('contact')} width="3">
                  Contact {sort === 'contact' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('channel')} width="2">
                  Channel {sort === 'channel' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('order_date')} width="3">
                  Order Date {sort === 'order_date' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('status')} width="2">
                  Status {sort === 'status' && <SortIcon />}
                </TableHeaderCell>
              </TableRow>
              <TableRow className="tools-filter">
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={id}
                      type="text"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      placeholder="Filter order #"
                      ref={idRef}
                      onChange={(e) => setId(e.target.value)}
                    />
                    {id && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setId('');
                          idRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={contact}
                      placeholder="Filter contact"
                      autoCorrect="off"
                      spellCheck="false"
                      ref={contactRef}
                      onChange={(e) => setContact(e.target.value)}
                    />
                    {contact && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setContact('');
                          contactRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={channel}
                      placeholder="Filter channel"
                      autoCorrect="off"
                      spellCheck="false"
                      ref={channelRef}
                      onChange={(e) => setChannel(e.target.value)}
                    />
                    {channel && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setChannel('');
                          channelRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-date-range">
                    <input
                      type="date"
                      aria-label="From"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                    <input
                      type="date"
                      aria-label="Till"
                      value={dateTill}
                      onChange={(e) => setDateTill(e.target.value)}
                    />
                    {(dateFrom || dateTill) && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setDateFrom('');
                          setDateTill('');
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow
                  className="clickable-row"
                  onClick={() => history.push(flattenToAppURL(item['@id']))}
                >
                  <TableCell>{item.id}</TableCell>
                  <TableCell>{item.contact}</TableCell>
                  <TableCell>{item.channel}</TableCell>
                  <TableCell>{formatDate(item.order_date)}</TableCell>
                  <TableCell className={statusClass(item.payment)}>
                    {item.status}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </article>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
OrdersView.propTypes = {
  content: PropTypes.shape({
    date: PropTypes.string,
    contact: PropTypes.string,
    channel: PropTypes.string,
  }).isRequired,
};

export default OrdersView;
